import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { OctagonAlert, TriangleAlert, X } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

interface ConfirmDeleteLearningObjectModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (objectId: number) => void;
  deleteData: { data: DeleteLearningObjectData; objectId: number };
}

export interface DeleteLearningObjectData {
  warns: string[];
  errors: string[];
  parents: {
    type: "COURSE" | "PATH";
    id: number;
  }[];
}

export const ConfirmDeleteLearningObjectModal: React.FC<
  ConfirmDeleteLearningObjectModalProps
> = ({ open, onClose, onConfirm, deleteData }) => {
  const theme = useTheme();

  const hasErrors = deleteData.data.errors.length > 0;
  const hasWarning = deleteData.data.warns.length > 0;
  const { t } = useTranslation("delete_modal");

  const formatError = (
    error: string,
    parents: {
      type: "COURSE" | "PATH";
      id: number;
    }[],
  ) => {
    if (error === "EPARENTS") {
      const paths = parents
        .filter((parent) => parent.type === "PATH")
        .map(({ id }) => id)
        .join(", ");
      const courses = parents
        .filter((parent) => parent.type === "COURSE")
        .map(({ id }) => id)
        .join(", ");
      return (
        <>
          {paths && paths.length ? (
            <li>{t("errors.EPARENTS_PATH", { paths })}</li>
          ) : undefined}
          {courses && courses.length ? (
            <li>{t("errors.EPARENTS_COURSE", { courses })}</li>
          ) : undefined}
        </>
      );
    }
    return <li>{t(`errors.${error}`)}</li>;
  };

  const formatWarning = (warn: string) => {
    return <li>{t(`warnings.${warn}`)}</li>;
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(4),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: 0,
          marginBottom: theme.spacing(3),
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{
            color: theme.palette.text.primary,
            zIndex: 1,
            padding: 0,
          }}
        >
          <X size={24} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(2),
          flexDirection: "column",
          padding: 0,
        }}
      >
        {hasErrors ? (
          <OctagonAlert size={48}></OctagonAlert>
        ) : (
          <TriangleAlert size={48}></TriangleAlert>
        )}
        <Typography sx={{ fontWeight: 700, fontSize: theme.spacing(3) }}>
          {hasErrors ? t("cancel_title") : t("confirm_title")}
        </Typography>
        {hasErrors ? (
          <>
            <Typography sx={{ fontSize: theme.spacing(2) }}>
              <Trans
                i18nKey={"error_pre"}
                ns={"delete_modal"}
                components={{ br: <br /> }}
              ></Trans>
              <ul style={{ margin: 0, padding: 0 }}>
                {deleteData.data.errors.map((error) =>
                  formatError(error, deleteData.data.parents),
                )}
              </ul>
            </Typography>
            <Typography
              sx={{ fontSize: theme.spacing(2), textAlign: "center" }}
            >
              <Trans
                i18nKey={"error_post"}
                ns={"delete_modal"}
                components={{ br: <br /> }}
              ></Trans>
            </Typography>
          </>
        ) : (
          <></>
        )}
        {hasWarning && !hasErrors ? (
          <>
            <Typography sx={{ fontSize: theme.spacing(2) }}>
              <Trans
                i18nKey={"warn_pre"}
                ns={"delete_modal"}
                components={{ br: <br /> }}
              ></Trans>
              <ul style={{ margin: 0, padding: 0 }}>
                {deleteData.data.warns.map(formatWarning)}
              </ul>
            </Typography>
            <Typography
              sx={{ fontSize: theme.spacing(2), textAlign: "center" }}
            >
              <Trans
                i18nKey={"warn_post"}
                ns={"delete_modal"}
                components={{ br: <br /> }}
              ></Trans>
            </Typography>
          </>
        ) : (
          <></>
        )}
        {!hasWarning && !hasErrors ? (
          <>
            <Typography sx={{ fontSize: theme.spacing(2) }}>
              <Trans
                i18nKey={"confirm_pre"}
                ns={"delete_modal"}
                components={{ br: <br /> }}
              ></Trans>
            </Typography>
            <Typography
              sx={{ fontSize: theme.spacing(2), textAlign: "center" }}
            >
              <Trans
                i18nKey={"confirm_post"}
                ns={"delete_modal"}
                components={{ br: <br /> }}
              ></Trans>
            </Typography>
          </>
        ) : (
          <></>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          padding: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
          paddingTop: theme.spacing(3),
          marginTop: theme.spacing(3),
          display: "flex",
          justifyContent: "stretch",
          gap: theme.spacing(1.5),
        }}
      >
        {hasErrors ? (
          <Button
            variant="outlined"
            sx={{
              width: "100%",
              color: theme.palette.text.primary,
              textTransform: "none",
              borderColor: theme.palette.text.primary,
              borderRadius: theme.spacing(1),
              "&:hover": {
                backgroundColor: "transparent",
                borderColor: theme.palette.text.primary,
              },
            }}
            onClick={() => onClose()}
          >
            {t("cancel")}
          </Button>
        ) : (
          <></>
        )}
        {!hasErrors ? (
          <>
            <Button
              variant="outlined"
              sx={{
                width: "100%",
                color: theme.palette.text.primary,
                textTransform: "none",
                borderColor: theme.palette.text.primary,
                borderRadius: theme.spacing(1),
                "&:hover": {
                  backgroundColor: "transparent",
                  borderColor: theme.palette.text.primary,
                },
              }}
              onClick={() => onClose()}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              sx={{
                width: "100%",
                textTransform: "none",
                borderRadius: theme.spacing(1),
              }}
              onClick={() => onConfirm(deleteData.objectId)}
            >
              {t("confirm")}
            </Button>
          </>
        ) : (
          <></>
        )}
      </DialogActions>
    </Dialog>
  );
};
